/**
 * 원격지원 입력 인코딩 — 담당자 뷰어의 마우스·키보드를 직원 PC로 보낼 이벤트로 바꾸는 순수 함수.
 *
 * ⚠ import를 넣지 말 것 — scripts/test-remote-input.js가 typescript.transpileModule로 통째로 평가한다.
 *
 * 이벤트 형식은 src-tauri/src/remote_support.rs의 `InputEvent`(serde tag "t")와 같아야 한다.
 *  - 좌표는 캡처 프레임(직원 PC의 모든 모니터 = 가상 데스크톱) 기준 0~1
 *    (레터박스·모니터 확대 보정은 여기서 끝낸다 — 직원 PC는 가상 데스크톱 픽셀로 펴기만 한다)
 *  - 인쇄 가능한 글자는 KEYEVENTF_UNICODE로 보내는 텍스트('x')로, 단축키·기능키는 KeyboardEvent.code('k')로
 */

export type RemoteInputEvent =
  | { t: 'm'; x: number; y: number; a: 0 | 1 | 2; b?: 0 | 1 | 2 }
  | { t: 'w'; x: number; y: number; dy?: number; dx?: number }
  | { t: 'k'; c: string; d: boolean }
  | { t: 'x'; s: string }
  | { t: 'p' };

export interface BoxRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 캡처 프레임 안의 사각형(0~1). 직원 PC는 모든 모니터를 한 프레임으로 보내므로 모니터 하나는 이 사각형 하나다 */
export interface NormRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const FULL_VIEW: NormRect = { x: 0, y: 0, w: 1, h: 1 };

/** 직원 PC의 화면 좌표(물리 픽셀) 사각형 — PcSummary의 capture·monitors */
export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 모니터마다 캡처 프레임(가상 데스크톱) 안의 위치 — 담당자 뷰어의 '화면 N' 확대에 쓴다 */
export function monitorViews(capture: ScreenRect, monitors: ScreenRect[]): NormRect[] {
  if (!(capture.width > 0 && capture.height > 0)) return [];
  const c01 = (v: number) => Math.min(1, Math.max(0, v));
  return monitors.map(m => {
    const x = c01((m.left - capture.left) / capture.width);
    const y = c01((m.top - capture.top) / capture.height);
    const r = c01((m.left + m.width - capture.left) / capture.width);
    const b = c01((m.top + m.height - capture.top) / capture.height);
    return { x, y, w: r - x, h: b - y };
  });
}

/**
 * 보기 사각형(view)을 상자에 object-contain으로 맞출 때, 영상 요소 '전체'가 놓일 자리(상자 기준 px).
 * 보기 사각형 바깥 부분은 상자 밖으로 밀려나므로 상자에 overflow:hidden을 건다. view가 전체면 기존 레터박스와 같다.
 */
export function viewLayout(
  boxWidth: number,
  boxHeight: number,
  videoWidth: number,
  videoHeight: number,
  view: NormRect = FULL_VIEW
): { left: number; top: number; width: number; height: number } | null {
  if (!(videoWidth > 0 && videoHeight > 0 && boxWidth > 0 && boxHeight > 0 && view.w > 0 && view.h > 0)) return null;
  const sw = view.w * videoWidth;
  const sh = view.h * videoHeight;
  const scale = Math.min(boxWidth / sw, boxHeight / sh);
  return {
    left: (boxWidth - sw * scale) / 2 - view.x * videoWidth * scale,
    top: (boxHeight - sh * scale) / 2 - view.y * videoHeight * scale,
    width: videoWidth * scale,
    height: videoHeight * scale,
  };
}

/**
 * 상자 위의 클릭 → '프레임 전체' 기준 0~1(확대 중이어도 직원 PC는 프레임 좌표만 안다).
 * 보기 사각형 밖(여백·확대 중 가려진 부분)이면 null — 보내지 않는다.
 * clamp를 켜면 보기 사각형 가장자리로 접는다(드래그 중 밖으로 나갔을 때 끝까지 끌 수 있게).
 */
export function viewPoint(
  clientX: number,
  clientY: number,
  box: BoxRect,
  videoWidth: number,
  videoHeight: number,
  view: NormRect = FULL_VIEW,
  clamp = false
): { x: number; y: number } | null {
  const l = viewLayout(box.width, box.height, videoWidth, videoHeight, view);
  if (!l) return null;
  let x = (clientX - box.left - l.left) / l.width;
  let y = (clientY - box.top - l.top) / l.height;
  const x1 = view.x + view.w;
  const y1 = view.y + view.h;
  if (clamp) {
    x = Math.min(x1, Math.max(view.x, x));
    y = Math.min(y1, Math.max(view.y, y));
    return { x, y };
  }
  if (x < view.x || x > x1 || y < view.y || y > y1) return null;
  return { x, y };
}

/**
 * object-contain으로 그린 영상 위의 클릭 → 영상 기준 0~1. 여백(레터박스)이면 null — 보내지 않는다.
 * clamp를 켜면 여백도 가장자리로 접는다(드래그 중 영상 밖으로 나갔을 때 끝까지 끌 수 있게).
 */
export function letterboxPoint(
  clientX: number,
  clientY: number,
  rect: BoxRect,
  videoWidth: number,
  videoHeight: number,
  clamp = false
): { x: number; y: number } | null {
  return viewPoint(clientX, clientY, rect, videoWidth, videoHeight, FULL_VIEW, clamp);
}

/** 휠 한 칸(Windows WHEEL_DELTA) */
export const WHEEL_DELTA = 120;

/**
 * 브라우저 wheel 이벤트 → WHEEL_DELTA 배수. 트랙패드처럼 잘게 오는 값은 모아서 한 칸이 되면 보낸다.
 * 부호: 브라우저는 아래로 굴리면 deltaY>0, Windows는 앞으로(위로) 굴리면 +라서 뒤집는다.
 */
export function createWheelAccumulator(pixelsPerNotch = 100) {
  let accY = 0;
  let accX = 0;
  // 줄 단위(deltaMode 1)는 휠 한 칸 = 3줄이 표준이다 — 3줄이 한 칸(100px)을 넘도록 줄당 34px
  const toPixels = (delta: number, mode: number) => (mode === 1 ? delta * 34 : mode === 2 ? delta * 400 : delta);
  return (deltaX: number, deltaY: number, deltaMode: number): { dx: number; dy: number } => {
    accY += toPixels(deltaY, deltaMode);
    accX += toPixels(deltaX, deltaMode);
    const stepsY = Math.trunc(accY / pixelsPerNotch);
    const stepsX = Math.trunc(accX / pixelsPerNotch);
    accY -= stepsY * pixelsPerNotch;
    accX -= stepsX * pixelsPerNotch;
    const clampSteps = (n: number) => Math.max(-10, Math.min(10, n));
    // 0에 -1을 곱하면 -0이 된다 — 직렬화·비교에서 혼란스러우니 0으로 맞춘다
    return {
      dy: stepsY === 0 ? 0 : -clampSteps(stepsY) * WHEEL_DELTA,
      dx: stepsX === 0 ? 0 : clampSteps(stepsX) * WHEEL_DELTA,
    };
  };
}

/** 마우스 버튼 번호(MouseEvent.button) → 직원 PC 버튼. 뒤로·앞으로(3·4)는 보내지 않는다 */
export function mouseButton(button: number): 0 | 1 | 2 | null {
  if (button === 0) return 0;
  if (button === 1) return 1;
  if (button === 2) return 2;
  return null;
}

const MODIFIER_CODES = new Set([
  'ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight',
]);

export interface KeyLike {
  key: string;
  code: string;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  isComposing?: boolean;
  keyCode?: number;
}

/**
 * keydown을 어떻게 보낼지.
 *  - 'ignore': IME 조합 중(한글) — 조합이 끝나면 compositionend/input이 글자를 준다
 *  - 'text'  : 인쇄 가능한 글자 — keydown에서는 보내지 않고 숨은 입력창의 input 이벤트로 받는다
 *              (Shift+a='A'처럼 글자가 확정된 뒤에 보내야 자판 배열이 달라도 맞는다)
 *  - 'vk'    : 수식키·기능키·단축키 — KeyboardEvent.code 그대로
 */
export function classifyKey(e: KeyLike): 'ignore' | 'text' | 'vk' {
  if (e.isComposing || e.keyCode === 229 || e.key === 'Process') return 'ignore';
  if (!e.code) return 'ignore';
  if (MODIFIER_CODES.has(e.code)) return 'vk';
  if (e.ctrlKey || e.altKey || e.metaKey) return 'vk';
  if (e.code === 'Space') return 'vk';
  if (e.key.length === 1) return 'text';
  return 'vk';
}

/** 텍스트 보내기 상한(직원 PC Rust의 MAX_TEXT_CHARS와 같다) */
export const MAX_SEND_TEXT = 500;

/** 담당자 PC에서 누를 수 없는(누르면 담당자 PC에서 동작하는) 조합 — 툴바 '특수 키' 메뉴 */
export interface SpecialKey {
  id: string;
  label: string;
  codes: string[];
}

export const SPECIAL_KEYS: SpecialKey[] = [
  { id: 'win', label: 'Windows 키 (시작 메뉴)', codes: ['MetaLeft'] },
  { id: 'win-r', label: 'Win+R (실행)', codes: ['MetaLeft', 'KeyR'] },
  { id: 'win-e', label: 'Win+E (파일 탐색기)', codes: ['MetaLeft', 'KeyE'] },
  { id: 'win-d', label: 'Win+D (바탕 화면)', codes: ['MetaLeft', 'KeyD'] },
  { id: 'alt-tab', label: 'Alt+Tab (창 전환)', codes: ['AltLeft', 'Tab'] },
  { id: 'ctrl-shift-esc', label: 'Ctrl+Shift+Esc (작업 관리자)', codes: ['ControlLeft', 'ShiftLeft', 'Escape'] },
  { id: 'esc', label: 'Esc', codes: ['Escape'] },
];

/** 조합 키 → 누름(순서대로) + 뗌(역순) */
export function comboEvents(codes: string[]): RemoteInputEvent[] {
  const down = codes.map(c => ({ t: 'k' as const, c, d: true }));
  const up = [...codes].reverse().map(c => ({ t: 'k' as const, c, d: false }));
  return [...down, ...up];
}

/** 텍스트 보내기 — 줄바꿈 통일 + 길이 상한 */
export function textEvents(text: string): RemoteInputEvent[] {
  const s = text.replace(/\r\n?/g, '\n');
  const chars = Array.from(s).slice(0, MAX_SEND_TEXT).join('');
  return chars ? [{ t: 'x', s: chars }] : [];
}

/** 직원 PC 집계용 — 이동·ping을 뺀 입력 건수(클릭·키·휠·글자) */
export function countMeaningfulInputs(events: RemoteInputEvent[]): number {
  let n = 0;
  for (const ev of events) {
    if (ev.t === 'p') continue;
    if (ev.t === 'm' && ev.a === 0) continue;
    n++;
  }
  return n;
}

/** 직원 PC 동의창이 쓰는 모니터 이름 — 화면 1(왼쪽)부터 */
export function monitorLabel(index: number, total: number, primary: boolean): string {
  const pos = total === 2 ? (index === 0 ? ' · 왼쪽' : ' · 오른쪽') : '';
  return `화면 ${index + 1}${primary ? ' (주 모니터)' : ''}${pos}`;
}
